import type { Meta } from '@cubejs-client/core'
import { X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import type { Editor } from '@tiptap/react'

import { OptionSelect } from '@/components/filters/OptionSelect'
import { MemberPicker, ViewPicker } from '@/components/member-picker/MemberPicker'
import { listMembers } from '@/components/member-picker/members'
import { Button } from '@/components/ui/button'
import { repeatOverAt } from '@/features/documents/nodes/resolve'
import { RELATIVE_RANGE_PATTERN } from '@/lib/format'
import { GRANULARITIES } from '@/lib/cube-report'
import { messages } from '@/lib/messages'

import {
  CheckboxField,
  NumberField,
  OrderEditor,
  RefSelect,
  SelectField,
  TextField,
  WhereEditor,
  type WhereItem,
} from './fields'
import { widgetAttrs } from './insert'
import { refOptions } from './refs'
import { useSelectedBlock } from './use-selected-block'
import type { DocumentTemplate } from './serialize'

const t = messages.documents.editor

function patchAttrs(editor: Editor, pos: number, patch: Record<string, unknown>) {
  editor
    .chain()
    .command(({ tr }) => {
      const node = tr.doc.nodeAt(pos)
      if (!node) return false
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...patch })
      return true
    })
    .run()
}

const str = (value: unknown) => (typeof value === 'string' ? value : '')
const list = (value: unknown) => (Array.isArray(value) ? (value as string[]) : [])
const whereOf = (value: unknown) => (Array.isArray(value) ? (value as WhereItem[]) : [])

interface PanelProps {
  meta: Meta
  attrs: Record<string, unknown>
  patch: (patch: Record<string, unknown>) => void
  template: DocumentTemplate
}

function CommonFields({ attrs, patch, template }: Omit<PanelProps, 'meta'>) {
  const ignored = list(attrs.ignoreFilters)
  const members = [...new Set(template.filters.map((filter) => filter.member))]
  return (
    <>
      <TextField
        label={t.block.blockTitle}
        value={str(attrs.title)}
        onChange={(value) => patch({ title: value || undefined })}
      />
      {members.length > 0 && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-muted-foreground mb-1 text-xs font-medium">
            {t.block.ignoreFilters}
          </legend>
          {members.map((member) => (
            <CheckboxField
              key={member}
              label={member}
              checked={ignored.includes(member)}
              onChange={(checked) => {
                const next = checked ? [...ignored, member] : ignored.filter((m) => m !== member)
                patch({ ignoreFilters: next.length > 0 ? next : undefined })
              }}
            />
          ))}
        </fieldset>
      )}
    </>
  )
}

type WidgetKind = 'reportKpi' | 'reportTable' | 'reportList' | 'reportChart'

function ViewField({
  kind,
  meta,
  attrs,
  patch,
}: Pick<PanelProps, 'meta' | 'attrs' | 'patch'> & { kind: WidgetKind }) {
  return (
    <ViewPicker
      meta={meta}
      value={str(attrs.view) || undefined}
      onChange={(view) => {
        // View değişince eski üyeler geçersiz kalır: yeni view'ın başlangıç üyeleriyle değişir.
        const next = widgetAttrs(kind, meta, view)
        const cleared = { measure: undefined, measures: undefined, dimensions: undefined }
        patch({ ...cleared, ...(next ?? { view }) })
      }}
    />
  )
}

function KpiSettings(props: PanelProps) {
  const { meta, attrs, patch } = props
  const view = str(attrs.view)
  return (
    <>
      <ViewField kind="reportKpi" {...props} />
      <MemberPicker
        meta={meta}
        view={view}
        kind="measure"
        value={str(attrs.measure) || undefined}
        onChange={(measure) => patch({ measure })}
      />
      <CommonFields {...props} />
    </>
  )
}

function TableSettings(props: PanelProps) {
  const { meta, attrs, patch } = props
  const view = str(attrs.view)
  const measures = list(attrs.measures)
  const dimensions = list(attrs.dimensions)
  return (
    <>
      <ViewField kind="reportTable" {...props} />
      <MemberPicker
        multiple
        meta={meta}
        view={view}
        kind="measure"
        value={measures}
        onChange={(next) => patch({ measures: next })}
      />
      <MemberPicker
        multiple
        meta={meta}
        view={view}
        kind="dimension"
        value={dimensions}
        onChange={(next) => patch({ dimensions: next })}
      />
      <OrderEditor
        label={t.block.order}
        value={list(attrs.order)}
        members={[...measures, ...dimensions]}
        onChange={(order) => patch({ order: order.length > 0 ? order : undefined })}
      />
      <WhereEditor
        label={t.block.where}
        value={whereOf(attrs.where)}
        members={listMembers(meta, view, 'dimension').map((option) => option.value)}
        onChange={(where) => patch({ where: where.length > 0 ? where : undefined })}
      />
      <CheckboxField
        label={t.block.totals}
        checked={attrs.totals === true}
        onChange={(totals) => patch({ totals: totals || undefined })}
      />
      <CommonFields {...props} />
    </>
  )
}

function ListSettings(props: PanelProps) {
  const { meta, attrs, patch } = props
  const view = str(attrs.view)
  const columns = list(attrs.columns)
  return (
    <>
      <ViewField kind="reportList" {...props} />
      <MemberPicker
        multiple
        meta={meta}
        view={view}
        kind="dimension"
        label={t.block.columns}
        value={columns}
        onChange={(next) => patch({ columns: next })}
      />
      <MemberPicker
        meta={meta}
        view={view}
        kind="dimension"
        label={t.block.rowKey}
        value={str(attrs.rowKey) || undefined}
        onChange={(rowKey) => patch({ rowKey })}
      />
      <OrderEditor
        label={t.block.defaultSort}
        value={list(attrs.defaultSort)}
        members={columns}
        onChange={(sort) => patch({ defaultSort: sort.length > 0 ? sort : undefined })}
      />
      <WhereEditor
        label={t.block.where}
        value={whereOf(attrs.where)}
        members={listMembers(meta, view, 'dimension').map((option) => option.value)}
        onChange={(where) => patch({ where: where.length > 0 ? where : undefined })}
      />
      <NumberField
        label={t.block.limit}
        value={typeof attrs.limit === 'number' ? attrs.limit : undefined}
        onChange={(limit) => patch({ limit })}
      />
      <MemberPicker
        multiple
        meta={meta}
        view={view}
        kind="measure"
        label={t.block.totalsMeasures}
        value={list(attrs.totals)}
        onChange={(next) => patch({ totals: next.length > 0 ? next : undefined })}
      />
      <CommonFields {...props} />
    </>
  )
}

interface Series {
  measure: string
  type: 'bar' | 'line'
  axis?: 'left' | 'right'
}

function RangeField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string | undefined) => void
}) {
  const [text, setText] = useState(value)
  const invalid = text !== '' && !RELATIVE_RANGE_PATTERN.test(text)
  return (
    <TextField
      label={t.block.range}
      value={text}
      hint={t.block.rangeHint}
      error={invalid ? t.block.rangeInvalid : undefined}
      onChange={(next) => {
        setText(next)
        if (next === '') onChange(undefined)
        else if (RELATIVE_RANGE_PATTERN.test(next)) onChange(next)
      }}
    />
  )
}

function ChartSettings(props: PanelProps) {
  const { meta, attrs, patch } = props
  const view = str(attrs.view)
  const series = (Array.isArray(attrs.series) ? attrs.series : []) as Series[]
  const setSeries = (next: Series[]) => patch({ series: next })
  return (
    <>
      <ViewField kind="reportChart" {...props} />
      <MemberPicker
        meta={meta}
        view={view}
        kind="time"
        value={str(attrs.time) || undefined}
        onChange={(time) => patch({ time })}
      />
      <SelectField
        label={t.block.granularity}
        value={str(attrs.granularity) || 'month'}
        onChange={(granularity) => patch({ granularity })}
        options={GRANULARITIES.map((granularity) => ({
          value: granularity,
          label: t.block.granularities[granularity],
        }))}
      />
      <div className="flex flex-col gap-1.5">
        <MemberPicker
          meta={meta}
          view={view}
          kind="measure"
          label={t.block.series}
          value={undefined}
          onChange={(measure) =>
            setSeries([
              ...series.filter((item) => item.measure !== measure),
              { measure, type: 'bar' },
            ])
          }
        />
        {series.map((item) => (
          <div
            key={item.measure}
            className="border-input flex items-center gap-1 rounded-sm border px-2 py-1 text-sm"
          >
            <span className="flex-1 truncate">{item.measure}</span>
            <OptionSelect
              size="sm"
              aria-label={t.block.seriesType(item.measure)}
              className="w-auto"
              value={item.type}
              options={[
                { value: 'bar', label: t.block.seriesBar },
                { value: 'line', label: t.block.seriesLine },
              ]}
              onValueChange={(next) =>
                setSeries(
                  series.map((other) =>
                    other === item ? { ...other, type: next as Series['type'] } : other,
                  ),
                )
              }
            />
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={t.block.removeSeries(item.measure)}
              onClick={() => setSeries(series.filter((other) => other !== item))}
            >
              <X />
            </Button>
          </div>
        ))}
      </div>
      <RangeField value={str(attrs.range)} onChange={(range) => patch({ range })} />
      <CheckboxField
        label={t.block.horizontal}
        checked={attrs.horizontal === true}
        onChange={(horizontal) => patch({ horizontal: horizontal || undefined })}
      />
      <CommonFields {...props} />
    </>
  )
}

export function BlockSettings({
  editor,
  meta,
  template,
}: {
  editor: Editor
  meta: Meta | undefined
  template: DocumentTemplate
}) {
  const selected = useSelectedBlock(editor)
  if (!selected) {
    return <p className="text-muted-foreground text-sm">{t.block.none}</p>
  }
  const { pos, type, attrs } = selected
  const patch = (next: Record<string, unknown>) => patchAttrs(editor, pos, next)
  const repeatOver = repeatOverAt(editor.state.doc, pos)
  const title = t.block.titles[type] ?? type

  let body: ReactNode
  switch (type) {
    case 'reportKpi':
    case 'reportTable':
    case 'reportList':
    case 'reportChart': {
      if (!meta) return <p className="text-muted-foreground text-sm">{messages.states.loading}</p>
      const props = { meta, attrs, patch, template }
      body =
        type === 'reportKpi' ? (
          <KpiSettings {...props} />
        ) : type === 'reportTable' ? (
          <TableSettings {...props} />
        ) : type === 'reportList' ? (
          <ListSettings {...props} />
        ) : (
          <ChartSettings {...props} />
        )
      break
    }
    case 'value':
    case 'change':
      body = (
        <RefSelect
          label={t.block.of}
          value={str(attrs.of)}
          options={refOptions(template, type, repeatOver)}
          onChange={(of) => patch({ of })}
        />
      )
      break
    case 'condition': {
      const numeric = typeof attrs.right === 'number'
      body = (
        <>
          <RefSelect
            label={t.block.left}
            value={str(attrs.left)}
            options={refOptions(template, 'left', repeatOver)}
            onChange={(left) => patch({ left })}
          />
          <SelectField
            label={t.block.op}
            value={str(attrs.op) || 'gt'}
            onChange={(op) => patch({ op })}
            options={(['gt', 'gte', 'lt', 'lte', 'eq'] as const).map((op) => ({
              value: op,
              label: t.block.ops[op],
            }))}
          />
          <SelectField
            label={t.block.rightKind}
            value={numeric ? 'number' : 'ref'}
            onChange={(kind) => {
              if (kind === 'number') patch({ right: 0 })
              else patch({ right: refOptions(template, 'right', repeatOver)[0]?.value ?? '' })
            }}
            options={[
              { value: 'number', label: t.block.rightNumber },
              { value: 'ref', label: t.block.rightRef },
            ]}
          />
          {numeric ? (
            <NumberField
              label={t.block.rightNumber}
              min={Number.MIN_SAFE_INTEGER}
              value={attrs.right as number}
              onChange={(right) => patch({ right: right ?? 0 })}
            />
          ) : (
            <RefSelect
              label={t.block.rightRef}
              value={str(attrs.right)}
              options={refOptions(template, 'right', repeatOver)}
              onChange={(right) => patch({ right })}
            />
          )}
        </>
      )
      break
    }
    case 'repeat': {
      const overs = Object.entries(template.queries).filter(([, q]) => q.dimensions.length > 0)
      body = (
        <>
          <SelectField
            label={t.block.over}
            value={str(attrs.over)}
            onChange={(over) => patch({ over })}
            options={[
              ...(overs.some(([name]) => name === attrs.over)
                ? []
                : [
                    {
                      value: str(attrs.over),
                      label: messages.memberPicker.notFound(str(attrs.over)),
                    },
                  ]),
              ...overs.map(([name]) => ({ value: name, label: name })),
            ]}
          />
          <NumberField
            label={t.block.limit}
            value={typeof attrs.limit === 'number' ? attrs.limit : undefined}
            onChange={(limit) => patch({ limit: limit ?? 50 })}
          />
        </>
      )
      break
    }
    case 'note':
      body = (
        <SelectField
          label={t.block.noteType}
          value={str(attrs.type) || 'info'}
          onChange={(next) => patch({ type: next })}
          options={[
            { value: 'info', label: messages.documents.nodes.note.info },
            { value: 'warning', label: messages.documents.nodes.note.warning },
          ]}
        />
      )
      break
    default:
      body = null
  }

  return (
    <section key={pos} aria-label={title} className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">{title}</h3>
      {body}
    </section>
  )
}
